import { AlertTriangle } from 'lucide-react'
import { t } from '@/lib/i18n'

export type LoadingShape = 'lines' | 'cards' | 'site' | 'camera'

/** One grey placeholder bar or block. Decorative only. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <span aria-hidden="true" className={`skeleton block rounded-md bg-subtle ${className}`} />
}

/** Three short text lines, used inside cards while their text loads. */
export function SkeletonLines({ lines = 3 }: { lines?: number }) {
  const widths = ['w-11/12', 'w-4/5', 'w-2/3', 'w-3/4']
  return (
    <span aria-hidden="true" className="block space-y-2.5">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={`h-3.5 ${widths[i % widths.length]}`} />
      ))}
    </span>
  )
}

function PhotoCardSkeleton() {
  return (
    <span className="block bg-card rounded-xl border border-border overflow-hidden">
      <Skeleton className="aspect-video w-full rounded-none" />
      <span className="block p-4 space-y-2.5">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3.5 w-3/4" />
      </span>
    </span>
  )
}

/**
 * Loading state shaped like what is coming, so the page doesn't jump when
 * the data arrives. The label stays for screen readers (and tests).
 */
export function LoadingCard({ label = t('Loading…'), shape = 'lines' }: { label?: string; shape?: LoadingShape }) {
  let body
  if (shape === 'cards') {
    body = (
      <span className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <PhotoCardSkeleton />
        <PhotoCardSkeleton />
        <PhotoCardSkeleton />
      </span>
    )
  } else if (shape === 'site') {
    body = (
      <span className="block">
        <Skeleton className="h-7 w-64 max-w-full mb-2" />
        <Skeleton className="h-4 w-40 mb-6" />
        <span className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <span className="block bg-card rounded-xl border border-border p-5">
            <Skeleton className="h-4 w-1/3 mb-4" />
            <SkeletonLines lines={4} />
          </span>
          <span className="block bg-card rounded-xl border border-border overflow-hidden">
            <Skeleton className="aspect-video w-full rounded-none" />
          </span>
        </span>
      </span>
    )
  } else if (shape === 'camera') {
    body = (
      <span className="block">
        <Skeleton className="h-7 w-56 max-w-full mb-6" />
        <span className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <span className="block lg:col-span-2 bg-card rounded-xl border border-border overflow-hidden">
            <Skeleton className="aspect-video w-full rounded-none" />
          </span>
          <span className="block bg-card rounded-xl border border-border p-5">
            <SkeletonLines lines={4} />
          </span>
        </span>
      </span>
    )
  } else {
    body = (
      <span className="block bg-card rounded-lg border border-border p-6">
        <SkeletonLines />
      </span>
    )
  }
  return (
    <output aria-busy="true" className="block">
      <span className="sr-only">{label}</span>
      {body}
    </output>
  )
}

interface ErrorCardProps {
  message?: string
  onRetry?: () => void
}

export function ErrorCard({ message = t('Something went wrong.'), onRetry }: ErrorCardProps) {
  return (
    <div
      role="alert"
      className="bg-card rounded-lg border border-critical/30 p-6 text-sm text-critical flex items-start gap-3"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="flex-1">
        <div className="font-medium">{message}</div>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 inline-flex items-center text-xs text-critical hover:opacity-80 underline"
          >
            {t('Retry')}
          </button>
        ) : null}
      </div>
    </div>
  )
}
